// Local DataRoom store (localStorage). Backend integration TBD —
// see frontend-hawk-v2/.claude/dataroom-backend-circleback.md.

const STORAGE_KEY = 'hawk:dataroom:v1';
const SAME_TAB_EVENT = 'hawk:dataroom:changed';

export type DataroomArticle = {
  articleId: number;
  title: string;
  url: string;
  summary: string;
  topics: string[];
  companies: string[];
  addedAt: string;
};

export type DataroomState = {
  articles: DataroomArticle[];
};

const EMPTY: DataroomState = { articles: [] };

function read(): DataroomState {
  if (typeof window === 'undefined') return EMPTY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<DataroomState> | null;
    if (!parsed || !Array.isArray(parsed.articles)) return EMPTY;
    return { articles: parsed.articles.filter(Boolean) as DataroomArticle[] };
  } catch {
    return EMPTY;
  }
}

function write(state: DataroomState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT));
  } catch {
    // ignore quota/serialization errors
  }
}

export function loadDataroom(): DataroomState {
  return read();
}

export function addArticleToDataroom(article: DataroomArticle): void {
  const state = read();
  const next = state.articles.filter((a) => a.articleId !== article.articleId);
  next.unshift(article);
  write({ articles: next });
}

export function removeArticleFromDataroom(articleId: number): void {
  const state = read();
  write({ articles: state.articles.filter((a) => a.articleId !== articleId) });
}

export function subscribeDataroom(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  const onSameTab = () => cb();
  window.addEventListener('storage', onStorage);
  window.addEventListener(SAME_TAB_EVENT, onSameTab as EventListener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(SAME_TAB_EVENT, onSameTab as EventListener);
  };
}

export function dedupeStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const label = v.trim();
    if (!label) continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}
